import React from 'react';
import { Box, xcss } from '@atlaskit/primitives';

import {
	type ActivityItemEdge,
	type ActivityNode,
	isDocumentActivity,
	isGraphActivity,
	parseThirdPartyARI,
	type QuickFind1PEntity,
	type QuickFindGraphEntity,
	type ExternalDocument,
	isRemoteLinkActivity,
	ExternalRemoteLink,
} from '@atlassian/search-client';
import { getGenericUrlWithAnalyticsQueryParams, transformAriToId } from '@atlassian/search-common';

import { dateFormatter } from '../utils/date-formatter';
import { ProductIcon, Products, type ProductKeys } from './icons/product-icon';
import { FormattedMessage, MessageDescriptor } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { messages } from './messages';

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

export const getDocumentSubtypeLabel = ({
	product,
	subtype,
}: {
	product?: string;
	subtype?: string;
}): MessageDescriptor | undefined => {
	if (!product || !subtype) {
		return;
	}
	const SUBTYPE_DOCUMENT = 'DOCUMENT';
	const SUBTYPE_PRESENTATION = 'PRESENTATION';
	const SUBTYPE_SPREADSHEET = 'SPREADSHEET';

	type DocumentSubtypeLabels = Record<string, MessageDescriptor>;
	const GOOGLE_LABELS: DocumentSubtypeLabels = {
		[SUBTYPE_DOCUMENT]: messages.googleDocsLabel,
		[SUBTYPE_PRESENTATION]: messages.googleSlidesLabel,
		[SUBTYPE_SPREADSHEET]: messages.googleSheetsLabel,
	};

	const MICROSOFT_LABELS: DocumentSubtypeLabels = {
		[SUBTYPE_DOCUMENT]: messages.msWordLabel,
		[SUBTYPE_PRESENTATION]: messages.msPowerpointLabel,
		[SUBTYPE_SPREADSHEET]: messages.msExcelLabel,
	};

	const normalizedSubtype = subtype?.toUpperCase();

	let label;
	if (product === Products.Sharepoint || product === Products.OneDrive) {
		label = MICROSOFT_LABELS[normalizedSubtype];
	} else if (product.includes(Products.GoogleDrive)) {
		label = GOOGLE_LABELS[normalizedSubtype];
	}

	return label;
};

export const getRemoteLinkSubtypeLabel = ({
	product,
	subtype,
}: {
	product?: string;
	subtype?: string;
}): MessageDescriptor | undefined => {
	if (!product || !subtype) {
		return;
	}

	const labelKey = `${subtype}Label` as keyof typeof messages;
	if (labelKey in messages) {
		return messages[labelKey];
	} else {
		return messages.otherLabel;
	}
};

const mapConfluenceActivityToSearchResult = (activity: ActivityNode<QuickFind1PEntity>) => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { title, space, links } = activity.object.data;

	const icon = <ProductIcon productKey={Products.Confluence} />;

	const spaceName = space?.name || '';

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;

	const urlBase = links?.base;
	const urlWebUi = links?.webUi;
	const url = urlBase && urlWebUi && `${urlBase}${urlWebUi}`;

	if (!title || !url) {
		return undefined;
	}

	return {
		id,
		url,
		icon,
		type: activity.object.type,
		title,
		meta: spaceName,
		containerId: transformAriToId(space?.id || 'UNAVAILABLE', 'space'),
		additionalMeta: timestamp ? dateFormatter(timestamp, true, eventType) : '',
	};
};

const mapJiraActivityToSearchResult = (activity: ActivityNode<QuickFind1PEntity>) => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { key, fieldsById, webUrl } = activity.object.data;

	const icon = <ProductIcon productKey={Products.Jira} />;

	const summary = fieldsById?.edges?.[0]?.node?.text;
	const title = key && summary && `${key}: ${summary}`;

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;

	const meta = <Box xcss={typeLabelContainer}>{activity.object.type}</Box>;

	if (!title || !webUrl) {
		return undefined;
	}

	return {
		id,
		url: webUrl,
		icon,
		type: activity.object.type,
		title,
		meta,
		containerId: '',
		additionalMeta: timestamp ? dateFormatter(timestamp, true, eventType) : '',
	};
};

const mapTownsquareActivityToSearchResult = (activity: ActivityNode<QuickFind1PEntity>) => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { name, key, url } = activity.object.data;

	const icon = <ProductIcon productKey={Products.Atlas} />;

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;

	if (!name || !url) {
		return undefined;
	}

	return {
		id,
		url,
		icon,
		type: activity.object.type,
		title: name,
		meta: key,
		containerId: '',
		additionalMeta: timestamp ? dateFormatter(timestamp, true, eventType) : '',
	};
};

const mapGraphDocumentActivityToSearchResult = (activity: ActivityNode<ExternalDocument>) => {
	if (!activity.object.data) {
		return;
	}
	const { type, displayName, parent, url } = activity.object.data;
	const id = activity.object.id;

	const { resourceOwner } = parseThirdPartyARI(id);
	const resourceType = resourceOwner.split('.').at(1);
	const product = resourceType === 'google-drive' ? 'drive' : resourceType || '';

	if (!Object.values(Products).includes(product as ProductKeys)) {
		return;
	}

	const title = displayName;
	if (!title || !url) {
		return undefined;
	}

	const icon = <ProductIcon productKey={product as ProductKeys} />;

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;

	const subtype = type?.category?.toLowerCase();
	const label = getDocumentSubtypeLabel({
		product,
		subtype,
	});

	const typeLabel = label ? (
		<Box xcss={typeLabelContainer}>
			<FormattedMessage {...label} />
		</Box>
	) : subtype ? (
		<Box xcss={typeLabelContainer}>
			{subtype.charAt(0).toUpperCase() + subtype.substring(1).toLowerCase()}
		</Box>
	) : null;

	return {
		id,
		url,
		icon,
		type,
		title,
		meta: typeLabel,
		containerId: parent?.id || 'UNAVAILABLE',
		additionalMeta: timestamp ? dateFormatter(timestamp, true, eventType) : '',
	};
};

const mapGraphRemoteLinkActivityToSearchResult = (activity: ActivityNode<ExternalRemoteLink>) => {
	if (!activity.object.data) {
		return;
	}
	const { displayName, category, url } = activity.object.data;
	const id = activity.object.id;

	const { resourceOwner } = parseThirdPartyARI(id);
	const resourceType = resourceOwner.split('.').at(1);
	const product = resourceType === 'google-drive' ? 'drive' : resourceType || '';

	if (!Object.values(Products).includes(product as ProductKeys)) {
		return;
	}

	const title = displayName;
	if (!title || !url) {
		return undefined;
	}

	const icon = <ProductIcon productKey={product as ProductKeys} />;

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;

	const subtype = category?.toLowerCase();
	const label = getRemoteLinkSubtypeLabel({
		product,
		subtype,
	});

	const typeLabel = label ? (
		<Box xcss={typeLabelContainer}>
			<FormattedMessage {...label} />
		</Box>
	) : subtype ? (
		<Box xcss={typeLabelContainer}>
			{subtype.charAt(0).toUpperCase() + subtype.substring(1).toLowerCase()}
		</Box>
	) : null;

	return {
		id,
		url,
		icon,
		type: category,
		title,
		meta: typeLabel,
		containerId: 'UNAVAILABLE',
		additionalMeta: timestamp ? dateFormatter(timestamp, true, eventType) : '',
	};
};

const mapGraphActivityToSearchResult = (activity: ActivityNode<QuickFindGraphEntity>) => {
	if (isDocumentActivity(activity.object)) {
		return mapGraphDocumentActivityToSearchResult(activity);
	}
	if (isRemoteLinkActivity(activity.object) && fg('smartlinks-qf-recents')) {
		return mapGraphRemoteLinkActivityToSearchResult(activity);
	}
	return undefined;
};

export const mapRecentActivitiesToSearchResult = (
	edge: ActivityItemEdge,
	searchSessionId: string,
) => {
	const activity = edge.node;
	const product = activity.object.product;
	const result = isGraphActivity(activity.object)
		? mapGraphActivityToSearchResult(activity)
		: product === 'confluence'
			? mapConfluenceActivityToSearchResult(activity)
			: product === 'jira'
				? mapJiraActivityToSearchResult(activity)
				: product === 'townsquare'
					? mapTownsquareActivityToSearchResult(activity)
					: undefined;

	if (!result) {
		return undefined;
	}

	const urlWithAnalyticsQueryParams = getGenericUrlWithAnalyticsQueryParams({
		url: result.url,
		searchSessionId,
	});

	return {
		...result,
		url: urlWithAnalyticsQueryParams,
	};
};
